import { useEffect, useMemo, useRef, useState } from "react";

import type { SkippedAttachment } from "./office/attachmentQueue";
import { collectEmail, toEmailPayload, type CollectedEmail } from "./office/emailSource";
import { ItemContext, useCurrentItem } from "./office/itemContext";
import { initOffice, type MailItemLike, type OfficeCtx } from "./office/officeShim";
import { useAuth } from "./auth/useAuth";
import type {
  FilingGroupResult,
  FilingRequest,
  ItemType,
  ProjectSearchRow,
} from "./api/types";
import { usePrecheckFiling, useCreateFiling } from "./hooks/useFilings";
import { useUserSettings } from "./settings/useSettings";
import { DuplicateWarning } from "./components/DuplicateWarning";
import { FilingProgress } from "./components/FilingProgress";
import { ItemTypeSelect, isRefNumberValid } from "./components/ItemTypeSelect";
import { LoginPane } from "./components/LoginPane";
import { ProjectPicker } from "./components/ProjectPicker";
import { RecentFilings } from "./components/RecentFilings";
import { SettingsPane } from "./components/SettingsPane";

export default function App() {
  const [officeCtx, setOfficeCtx] = useState<OfficeCtx | null>(null);
  const [officeError, setOfficeError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    initOffice()
      .then((ctx) => {
        if (!cancelled) {
          setOfficeCtx(ctx);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setOfficeError(
            err instanceof Error ? err.message : "Failed to initialize Office.js",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const itemContext = useMemo(
    () => (officeCtx ? new ItemContext(officeCtx) : null),
    [officeCtx],
  );
  useEffect(() => () => itemContext?.dispose(), [itemContext]);

  if (officeCtx && itemContext) {
    return <FilerApp officeCtx={officeCtx} itemContext={itemContext} />;
  }
  if (officeError) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p role="alert" className="text-sm text-red-600">
          {officeError}
        </p>
      </div>
    );
  }
  return (
    <div className="flex h-full items-center justify-center p-6">
      <p className="text-sm text-slate-400">Loading…</p>
    </div>
  );
}

interface FilerAppProps {
  officeCtx: OfficeCtx;
  itemContext: ItemContext;
}

function FilerApp({ officeCtx, itemContext }: FilerAppProps) {
  const auth = useAuth();
  const currentItem = useCurrentItem(itemContext);
  const userSettings = useUserSettings(officeCtx);

  const [activeTab, setActiveTab] = useState<"file" | "recent" | "settings">(
    "file",
  );
  const [selectedProjects, setSelectedProjects] = useState<ProjectSearchRow[]>(
    [],
  );
  const [itemType, setItemType] = useState<ItemType>(
    userSettings.settings.defaultItemType,
  );
  const [refNumber, setRefNumber] = useState("");

  const searchFilters = useMemo(
    () => ({
      divisions: userSettings.settings.divisions,
      years: userSettings.settings.years,
      pathPrefix: userSettings.settings.pathPrefix,
    }),
    [
      userSettings.settings.divisions,
      userSettings.settings.years,
      userSettings.settings.pathPrefix,
    ],
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState<{
    collected: CollectedEmail;
    duplicates: { project_id: number; filed_by: string | null; filed_at: string | null }[];
  } | null>(null);
  const [filingResult, setFilingResult] = useState<FilingGroupResult | null>(
    null,
  );
  const [filingError, setFilingError] = useState<string | null>(null);
  const [skippedAttachments, setSkippedAttachments] = useState<
    SkippedAttachment[]
  >([]);

  const precheckMutation = usePrecheckFiling();
  const createFilingMutation = useCreateFiling();

  // Read through a ref so changing the default in Settings doesn't itself
  // reset the form — this effect must fire on item change and nothing else.
  const defaultItemTypeRef = useRef(userSettings.settings.defaultItemType);
  defaultItemTypeRef.current = userSettings.settings.defaultItemType;

  // A different email is now selected (or none is) — misfiling into the
  // wrong project is worse than an extra click, so nothing about the
  // previous email's filing flow carries over.
  useEffect(() => {
    setSelectedProjects([]);
    setItemType(defaultItemTypeRef.current);
    setRefNumber("");
    setDuplicateInfo(null);
    setFilingResult(null);
    setFilingError(null);
    setSkippedAttachments([]);
  }, [currentItem?.internetMessageId]);

  const projectLabels = useMemo(
    () =>
      new Map(selectedProjects.map((p) => [p.id, p.project_code ?? p.name])),
    [selectedProjects],
  );

  async function submitFiling(collected: CollectedEmail): Promise<void> {
    setIsSubmitting(true);
    setDuplicateInfo(null);
    setFilingError(null);
    try {
      const request: FilingRequest = {
        client_request_id: crypto.randomUUID(),
        item_type: itemType,
        ref_number: itemType === "email" ? undefined : refNumber.trim(),
        project_ids: selectedProjects.map((p) => p.id),
        email: toEmailPayload(collected),
      };
      const result = await createFilingMutation.mutateAsync(request);
      setFilingResult(result);
    } catch (err) {
      setFilingError(err instanceof Error ? err.message : "Filing failed");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleFileClick(): Promise<void> {
    if (!currentItem) {
      return;
    }
    setFilingError(null);
    setFilingResult(null);
    setIsSubmitting(true);
    try {
      const collected = await collectEmail(currentItem);
      setSkippedAttachments(collected.skippedAttachments);
      const precheckResult = await precheckMutation.mutateAsync({
        internet_message_id: collected.internetMessageId,
        project_ids: selectedProjects.map((p) => p.id),
      });
      const duplicates = precheckResult.results.filter((r) => r.duplicate);
      if (duplicates.length > 0) {
        setDuplicateInfo({ collected, duplicates });
        setIsSubmitting(false);
        return;
      }
      await submitFiling(collected);
    } catch (err) {
      setFilingError(err instanceof Error ? err.message : "Filing failed");
      setIsSubmitting(false);
    }
  }

  if (!auth.isAuthenticated) {
    return (
      <LoginPane
        onLogin={auth.login}
        isLoggingIn={auth.isLoggingIn}
        error={auth.loginError}
      />
    );
  }

  const canFile =
    currentItem !== null &&
    selectedProjects.length > 0 &&
    isRefNumberValid(itemType, refNumber) &&
    !isSubmitting;

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex gap-1 border-b border-slate-200">
        <TabButton active={activeTab === "file"} onClick={() => setActiveTab("file")}>
          File
        </TabButton>
        <TabButton
          active={activeTab === "recent"}
          onClick={() => setActiveTab("recent")}
        >
          Recent
        </TabButton>
        <TabButton
          active={activeTab === "settings"}
          onClick={() => setActiveTab("settings")}
        >
          Settings
        </TabButton>
      </div>

      {activeTab === "recent" ? (
        <RecentFilings />
      ) : activeTab === "settings" ? (
        <SettingsPane
          settings={userSettings.settings}
          onChange={userSettings.update}
          isSaving={userSettings.isSaving}
          saveError={userSettings.saveError}
        />
      ) : !currentItem ? (
        <p className="text-sm text-slate-400">Open an email to file it.</p>
      ) : (
        <>
          <EmailHeaderCard item={currentItem} />

          <ProjectPicker
            selectedProjects={selectedProjects}
            onSelectionChange={setSelectedProjects}
            filters={searchFilters}
          />

          <ItemTypeSelect
            itemType={itemType}
            refNumber={refNumber}
            onItemTypeChange={setItemType}
            onRefNumberChange={setRefNumber}
          />

          {skippedAttachments.length > 0 && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
              {skippedAttachments.map((skipped) => (
                <p key={skipped.name}>
                  Not filed: cloud attachment — {skipped.name}
                </p>
              ))}
            </div>
          )}

          {duplicateInfo && (
            <DuplicateWarning
              duplicates={duplicateInfo.duplicates.map((d) => ({
                projectId: d.project_id,
                projectLabel:
                  projectLabels.get(d.project_id) ?? `Project #${d.project_id}`,
                filedBy: d.filed_by,
                filedAt: d.filed_at,
              }))}
              onFileAnyway={() => void submitFiling(duplicateInfo.collected)}
              onCancel={() => {
                setDuplicateInfo(null);
                setIsSubmitting(false);
              }}
            />
          )}

          <button
            type="button"
            onClick={() => void handleFileClick()}
            disabled={!canFile}
            className="rounded-md bg-blue-700 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? "Filing…" : "File"}
          </button>

          {filingError && (
            <p role="alert" className="text-sm text-red-600">
              {filingError}
            </p>
          )}

          {filingResult && (
            <FilingProgress result={filingResult} projectLabels={projectLabels} />
          )}
        </>
      )}
    </div>
  );
}

function EmailHeaderCard({ item }: { item: MailItemLike }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <p className="truncate text-sm font-semibold text-slate-900">
        {item.subject || "(no subject)"}
      </p>
      <p className="truncate text-xs text-slate-500">
        From {item.from.displayName || item.from.emailAddress}
      </p>
      <p className="text-xs text-slate-400">
        {new Date(item.sentDateTime).toLocaleString()}
      </p>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium ${
        active
          ? "border-b-2 border-blue-700 text-blue-800"
          : "text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}
